function extractPageFid(data: Record<string, any> | undefined | null): string | null {
  if (!data) return null
  const formFid =
    data?.form?.form?._attributes?.fid ??
    data?.form?._attributes?.fid ??
    data?.form?.fid ??
    data?.$fid
  if (formFid != null && String(formFid).trim()) return String(formFid).trim()
  const stateFid = data?.formState?.fid ?? data?.formState?._attributes?.fid
  if (stateFid != null && String(stateFid).trim()) return String(stateFid).trim()
  return null
}

function readWorkflowTextNode(value: unknown): string {
  if (value == null) return ''
  if (Array.isArray(value)) return readWorkflowTextNode(value[0])
  if (typeof value === 'object' && '_text' in value) {
    return String((value as { _text?: unknown })._text ?? '').trim()
  }
  return String(value).trim()
}

function workflowFieldFingerprint(value: unknown, seen = new WeakSet<object>()): unknown {
  if (value == null || typeof value !== 'object') return value
  if (seen.has(value)) return '[Circular]'
  seen.add(value)

  if (Array.isArray(value)) {
    return value.map((item) => workflowFieldFingerprint(item, seen))
  }

  const record = value as Record<string, unknown>
  if ('value' in record && 'type' in record) {
    return {
      type: record.type,
      value: workflowFieldFingerprint(record.value, seen),
    }
  }

  return Object.keys(record)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = workflowFieldFingerprint(record[key], seen)
      return acc
    }, {})
}

function summarizeWorkflowFields(fields: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!fields || typeof fields !== 'object') return {}
  return Object.keys(fields).sort().reduce<Record<string, unknown>>((acc, key) => {
    acc[key] = workflowFieldFingerprint(fields[key])
    return acc
  }, {})
}

function summarizeWorkflowStates(states: unknown): unknown[] {
  if (!Array.isArray(states)) return []
  return states.slice(0, 20).map((state) => {
    const s = state as Record<string, unknown>
    return {
      id: s.element_id,
      name: s.element_name,
      level: s.element_level,
      refill: s.is_refill,
      empty: s.is_empty,
      visible: s.is_visible,
      enabled: s.is_enabled,
    }
  })
}

export function summarizePageDataForCommit(
  id: string,
  data: Record<string, any> | undefined | null,
) {
  const fields = data?.uniData?.fields?.fields as Record<string, unknown> | undefined
  const changedFields = data?.jsResult?.changed_data?.fields?.fields as Record<string, unknown> | undefined
  return {
    id,
    fid: extractPageFid(data),
    sessionId: data?.sessionId,
    cloneOf: data?.cloneOf ?? data?.$cloneOf,
    jsFormId: data?.jsResult?.form_id,
    hasForm: Boolean(data?.form),
    formId:
      data?.form?.form?._attributes?.id ??
      data?.form?._attributes?.id ??
      data?.formId,
    formTitle: readWorkflowTextNode(data?.form?.title ?? data?.form?.form?.title),
    fieldKeys: Object.keys(fields ?? {}).slice(0, 40),
    fieldSummary: summarizeWorkflowFields(fields),
    changedFieldKeys: Object.keys(changedFields ?? {}).slice(0, 40),
    changedFieldSummary: summarizeWorkflowFields(changedFields),
    states: summarizeWorkflowStates(data?.jsResult?.states),
  }
}

export function buildPageDataCommitSignature(
  id: string,
  data: Record<string, any> | undefined | null,
): string {
  try {
    return JSON.stringify(summarizePageDataForCommit(id, data))
  } catch {
    return String(Date.now())
  }
}

export function isPageDataCommitNoop(
  id: string,
  oldData: Record<string, any> | undefined | null,
  newData: Record<string, any> | undefined | null,
): boolean {
  if (!oldData || !newData) return false
  return buildPageDataCommitSignature(id, oldData) === buildPageDataCommitSignature(id, newData)
}
